import { PRIORIDAD, anclar } from "../ui/dock.js";
import { registrarAuditoria, registrarCambioConfig } from "./auditLog.js";
import { AURA_ISOTIPO, AURA_LOGO, BRAND, DEFAULT_PARAMETROS_FISCALES, DEFAULT_PARAMETROS_NOMINA, EMPRESA, KEYS, PARAMETROS_FISCALES_KEY, PARAMETROS_NOMINA_KEY, PUC_DEFAULT } from "./config.js";
import { CLOUD_TABLE_KEYS, ENTITY_SCHEMA } from "./entitySchema.js";
import { state } from "./state.js";
import { cloudState, supa } from "./supabaseClient.js";
import { addDias, fmtDate, genId, todayCO } from "./utils.js";
import { render } from "../ui/app.js";
import { refrescarFormatoDoc } from "./formatoDoc.js";
import { refrescarSplashConfig } from "../ui/splash.js";


// ─── CONFIGURACIONES EN LA NUBE (tabla tenant_config) ───────────────────────
// Fuente de verdad de cada configuración: una fila por (tenant, clave) en la
// tabla tenant_config. localStorage queda solo como caché local de lectura,
// así el ERP vive 100% en la nube y no depende del navegador de un equipo.
// El snapshot completo de localStorage (tabla erp_data) quedó RETIRADO: las
// entidades de negocio se sincronizan por tabla propia (saveData) y las
// configuraciones por tenant_config; erp_data solo se lee una vez al iniciar
// sesión como respaldo de datos anteriores a esta migración.
export const CONFIG_CLOUD_KEYS = new Set([
  "erp:alert_config", "erp:empresa_config", "erp:ia_config",
  "erp:parametros_fiscales", "erp:parametros_nomina", "erp:tabla_comisiones",
  "erp:roles_personalizados", "erp:puesta_en_marcha", "erp:splash_config",
  "erp:politica_password", "erp:politica_sesion", "erp:roles_sistema",
  "erp:formato_documentos", "erp:dataico_config", "erp:costos_via"
]);

// Avance del asistente de Puesta en Marcha: { manual:{}, noAplica:{}, ocultarBanner }
export function getPuestaEnMarcha() {
  try {
    const raw = localStorage.getItem("erp:puesta_en_marcha");
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function savePuestaEnMarcha(cfg) {
  try { localStorage.setItem("erp:puesta_en_marcha", JSON.stringify(cfg)); } catch {}
}

export const ROLES_PERSONALIZADOS_KEY = "erp:roles_personalizados";

// Ajustes POR EMPRESA de los permisos de los roles de sistema:
// { [rolId]: { paginas: [pageId] } }. Sin entrada para un rol, rigen los
// permisos de fábrica (ROLE_PAGES). "admin" nunca se ajusta: acceso total.
export const ROLES_SISTEMA_KEY = "erp:roles_sistema";
export function getRolesSistemaOverrides() {
  try {
    const raw = localStorage.getItem(ROLES_SISTEMA_KEY);
    const o = raw ? JSON.parse(raw) : {};
    return o && typeof o === "object" && !Array.isArray(o) ? o : {};
  } catch { return {}; }
}
export function saveRolesSistemaOverrides(o) {
  try { localStorage.setItem(ROLES_SISTEMA_KEY, JSON.stringify(o || {})); } catch { /* sin caché local */ }
}

// Roles personalizados del tenant: [{ id, nombre, paginas:[pageId], mfa_obligatorio }]
// Se leen del caché local (sincronizado con tenant_config como cualquier config).
export function getRolesPersonalizados() {
  try {
    const raw = localStorage.getItem(ROLES_PERSONALIZADOS_KEY);
    const lista = raw ? JSON.parse(raw) : [];
    return Array.isArray(lista) ? lista : [];
  } catch { return []; }
}

export function saveRolesPersonalizados(lista) {
  try { localStorage.setItem(ROLES_PERSONALIZADOS_KEY, JSON.stringify(lista)); } catch {}
}

// Carga SOLO los roles personalizados desde la nube. Se usa justo después del
// login, ANTES del chequeo de MFA obligatorio (que ocurre antes de
// cargarConfigsNube), para que un rol personalizado con MFA exigido no pueda
// saltarse la verificación por tener el caché local vacío o desactualizado.
export async function cargarRolesPersonalizadosNube() {
  if (!cloudState.tenantId) return;
  const { data, error } = await supa.from("tenant_config")
    .select("valor").eq("tenant_id", cloudState.tenantId)
    .eq("clave", ROLES_PERSONALIZADOS_KEY).maybeSingle();
  if (error) { console.error("⚠️ Error cargando roles personalizados:", error.message); return; }
  if (data && Array.isArray(data.valor)) {
    try { __lsSetItemOriginal(ROLES_PERSONALIZADOS_KEY, JSON.stringify(data.valor)); } catch {}
  }
}

// Nota: Storage.prototype es compartido entre localStorage y sessionStorage, así que
// __nativeStorageSetItem debe invocarse con el "this" correcto (call) para que la sesión
// de Supabase (sessionStorage) no termine escribiéndose por error en localStorage.
export const __nativeStorageSetItem = Storage.prototype.setItem;
const __lsSetItemOriginal = __nativeStorageSetItem.bind(localStorage);
Storage.prototype.setItem = function(key, value) {
  __nativeStorageSetItem.call(this, key, value);
  if (this === localStorage && CONFIG_CLOUD_KEYS.has(key)) programarSincronizacionConfig(key, value);
};

const __configTimers = {};
const __configPendientes = new Set();     // claves con debounce aún sin disparar
const __sincronizacionesEnVuelo = new Set(); // peticiones ya lanzadas, sin respuesta
const __cadenaSyncPorEntidad = {}; // key -> última promesa de sync, para encadenar

// Encadena la sincronización de una entidad para que dos saveData seguidos de la
// MISMA entidad NUNCA corran a la vez. Sin esto, ambas llamadas leen el mismo
// snapshot anterior, ven al padre como modificado y cada una hace delete+insert
// de las filas hijas: al solaparse, los delete corren antes que los insert y
// quedan filas duplicadas (todas en posicion 0). Ej.: una factura que se guarda
// al emitirla y otra vez al adjuntarle el asiento terminaba con sus ítems
// repetidos. Serializar por entidad garantiza que la 2ª sync vea el resultado
// de la 1ª (delete + 1 insert = una sola copia).
function encadenarSyncEntidad(key, val) {
  const previa = __cadenaSyncPorEntidad[key] || Promise.resolve();
  const siguiente = previa.catch(() => {}).then(() => sincronizarEntidadNube(key, val));
  __cadenaSyncPorEntidad[key] = siguiente;
  // Al terminar, soltar la referencia si nadie encadenó después (evita retener la última promesa)
  siguiente.finally(() => { if (__cadenaSyncPorEntidad[key] === siguiente) __cadenaSyncPorEntidad[key] = null; });
  return siguiente;
}

// Registra una promesa de sincronización para poder esperarla si hace falta
// recargar la página (aviso de versión nueva): una petición en vuelo que se
// corta con la recarga se perdería en la nube aunque localStorage ya la tenga.
function rastrearSincronizacion(promesa) {
  __sincronizacionesEnVuelo.add(promesa);
  promesa.finally(() => __sincronizacionesEnVuelo.delete(promesa));
  return promesa;
}

export function programarSincronizacionConfig(clave, valorString) {
  if (!cloudState.tenantId || cloudState.syncEnPausa) return;
  clearTimeout(__configTimers[clave]);
  __configPendientes.add(clave);
  __configTimers[clave] = setTimeout(() => {
    __configPendientes.delete(clave);
    rastrearSincronizacion(sincronizarConfigNube(clave, valorString));
  }, 800);
}

// Empuja a la nube TODO lo que esté esperando (debounce sin disparar y
// peticiones en vuelo). Se llama antes de recargar la página por una
// actualización, para que la recarga no deje el último tramo de trabajo solo
// en el localStorage de este navegador.
export async function vaciarSincronizacionPendiente() {
  const claves = [...__configPendientes];
  __configPendientes.clear();
  for (const clave of claves) {
    clearTimeout(__configTimers[clave]);
    const valor = localStorage.getItem(clave);
    if (valor != null) rastrearSincronizacion(sincronizarConfigNube(clave, valor));
  }
  await Promise.allSettled([...__sincronizacionesEnVuelo]);
}

// El último valor de cada configuración que se sabe que está en la nube: es el
// «antes» de la bitácora cuando alguien la cambia.
const __configEnNube = new Map();

export async function sincronizarConfigNube(clave, valorString) {
  if (!cloudState.tenantId) return;
  let valor;
  try { valor = JSON.parse(valorString); } catch { return; }
  const { error } = await supa.from("tenant_config").upsert({
    tenant_id: cloudState.tenantId, clave, valor,
    updated_at: new Date().toISOString(), updated_by: state.usuarioEmail || null
  }, { onConflict: "tenant_id,clave" });
  if (error) { console.error(`⚠️ Error sincronizando configuración ${clave}:`, error.message); return; }
  const antes = __configEnNube.has(clave) ? __configEnNube.get(clave) : null;
  __configEnNube.set(clave, valor);
  registrarCambioConfig(clave, antes, valor);
  mostrarIndicadorSync();
}

// Descarga todas las configuraciones del tenant al caché local ANTES de
// loadData(), para que los lectores existentes sigan funcionando sin cambios.
// Si una clave del caché aún no existe en la tabla (primer arranque tras la
// migración), se siembra desde el caché para que la nube quede autoritativa.
export async function cargarConfigsNube() {
  if (!cloudState.tenantId) return;
  const { data, error } = await supa.from("tenant_config").select("clave, valor").eq("tenant_id", cloudState.tenantId);
  if (error) { console.error("⚠️ Error cargando configuraciones de la nube:", error.message); return; }
  const enNube = new Set();
  (data || []).forEach(row => {
    if (!CONFIG_CLOUD_KEYS.has(row.clave)) return;
    enNube.add(row.clave);
    __configEnNube.set(row.clave, row.valor);
    try { __lsSetItemOriginal(row.clave, JSON.stringify(row.valor)); } catch {}
  });
  for (const clave of CONFIG_CLOUD_KEYS) {
    if (enNube.has(clave)) continue;
    const local = localStorage.getItem(clave);
    if (local) await sincronizarConfigNube(clave, local);
  }
}

// Cambio de configuración hecho desde otra sesión/dispositivo (Realtime):
// actualizar el caché local y el estado en memoria.
export function aplicarConfigRealtime(payload) {
  const row = payload.new;
  if (!row || !CONFIG_CLOUD_KEYS.has(row.clave)) return;
  __configEnNube.set(row.clave, row.valor);
  try { __lsSetItemOriginal(row.clave, JSON.stringify(row.valor)); } catch {}
  rehidratarConfigEnMemoria(row.clave);
  render();
}

// El logo que usa toda la app (login, dashboard, encabezados y documentos
// impresos) es el que la empresa subió en Configuración (EMPRESA.logo). Si no
// hay logo — instalación nueva o recién reiniciada — se usa la marca base del
// producto (Aura); cada cliente la reemplaza cargando su propio logo.
export function aplicarLogoEmpresa() {
  window.LOGO = EMPRESA.logo || AURA_LOGO;
  window.ISOTIPO = EMPRESA.logo || AURA_ISOTIPO;
}

export function rehidratarConfigEnMemoria(clave) {
  try {
    const raw = localStorage.getItem(clave);
    if (!raw) return;
    const val = JSON.parse(raw);
    if (clave === "erp:alert_config") state.alertConfig = { ...state.alertConfig, ...val };
    else if (clave === "erp:empresa_config") { Object.assign(EMPRESA, val); aplicarLogoEmpresa(); }
    else if (clave === "erp:ia_config") { state.iaConfig = { ...state.iaConfig, ...val }; delete state.iaConfig.apiKey; }
    else if (clave === "erp:splash_config") refrescarSplashConfig(); // la caché en memoria del splash queda obsoleta
    else if (clave === "erp:formato_documentos") refrescarFormatoDoc();  // ídem para el formato impreso
    // parámetros fiscales/nómina y tabla de comisiones se leen del caché en cada uso
  } catch {}
}

export function mostrarIndicadorSync() {
  let el = document.getElementById("__sync_indicator");
  if (!el) {
    el = document.createElement("div");
    el.id = "__sync_indicator";
    el.style.cssText = "background:#0E7490;color:white;font-size:11px;font-weight:700;padding:6px 12px;border-radius:20px;box-shadow:0 4px 12px rgba(0,0,0,.15);opacity:0;transition:opacity .3s;font-family:sans-serif;";
    el.textContent = "☁️ Guardado en la nube";
    // En la esquina compartida de avisos: antes se posicionaba solo y quedaba
    // debajo del recordatorio de pendientes o del aviso de versión nueva.
    anclar(el, PRIORIDAD.sync);
  }
  el.style.opacity = "1";
  clearTimeout(el._t);
  el._t = setTimeout(() => { el.style.opacity = "0"; }, 1800);
  // Un guardado exitoso limpia cualquier aviso de error pendiente.
  const err = document.getElementById("__sync_error");
  if (err) err.remove();
}

// Aviso VISIBLE y persistente cuando una sincronización falla. Antes el error
// solo iba a la consola: una subida rechazada (p. ej. por un desajuste de
// esquema) quedaba invisible y el documento vivía solo en ese navegador.
export function mostrarErrorSync(key) {
  let el = document.getElementById("__sync_error");
  if (!el) {
    el = document.createElement("div");
    el.id = "__sync_error";
    el.style.cssText = "background:#b91c1c;color:white;font-size:11px;font-weight:700;padding:6px 12px;border-radius:20px;box-shadow:0 4px 12px rgba(0,0,0,.2);font-family:sans-serif;max-width:280px;";
    anclar(el, PRIORIDAD.seguridad);
  }
  el.textContent = "⚠️ No se pudo guardar en la nube (" + key + "). Reintentando…";
}

export async function descargarSnapshotDeNube(tenantId) {
  const { data, error } = await supa
    .from("erp_data")
    .select("data")
    .eq("tenant_id", tenantId)
    .single();
  if (error) { console.error("⚠️ Error descargando de Supabase:", error.message); return; }
  if (data && data.data) {
    Object.entries(data.data).forEach(([k, v]) => {
      if (k.startsWith("sb-")) return; // nunca restaurar tokens de sesión desde la nube
      if (k === "erp:dataico_config") return; // API key de Dataico: no restaurar credenciales desde la nube
      if (v !== null && v !== undefined) __lsSetItemOriginal(k, v);
    });
  }
}

export async function obtenerTenantIdDelUsuario(userId) {
  const { data, error } = await supa
    .from("user_profiles")
    .select("tenant_id, rol, nombre")
    .eq("id", userId)
    .single();
  if (error) { console.error("⚠️ No se encontró perfil para este usuario:", error.message); return null; }
  return data;
}

export let __ultimoSnapshotNube = {};

// Serie JSON de cada registro del snapshot, indexada por id. Existe para no
// volver a serializar el snapshot completo en cada guardado: las fotos de los
// productos son base64 y hoy pesan 35 MB entre 91 fichas, así que recorrer todo
// en cada saveData congelaba la aplicación.
const __serieSnapshot = {};

// Única puerta para dejar fijado "lo que la nube ya tiene". `series` es opcional
// y permite reutilizar las cadenas ya calculadas por quien llama.
function fijarSnapshot(key, objetos, series) {
  const cfgS = ENTITY_SCHEMA[key] || {};
  const idF = cfgS.idField || "id";
  __ultimoSnapshotNube[key] = objetos;
  const m = new Map();
  objetos.forEach((o, i) => m.set(o[idF], (series && series[i]) || JSON.stringify(o)));
  __serieSnapshot[key] = m;
}

// Carga todas las entidades del tenant. Son 17 tablas principales + 11 tablas
// hijas = 28 consultas, TODAS independientes entre sí (cada una filtra por
// tenant_id). Antes se hacían una tras otra: 28 viajes de ida y vuelta en fila,
// que a ~200ms cada uno son varios segundos de espera al entrar. Ahora se
// lanzan en una sola tanda paralela y se ensamblan al final.
export async function cargarEntidadesNube() {
  if (!cloudState.tenantId) return;
  const keys = [...CLOUD_TABLE_KEYS];

  const peticiones = [];
  for (const key of keys) {
    const cfg = ENTITY_SCHEMA[key];
    peticiones.push(
      supa.from(cfg.table).select("*").eq("tenant_id", cloudState.tenantId)
        .then(r => ({ tipo: "padre", key, data: r.data, error: r.error }))
    );
    for (const child of (cfg.childTables || [])) {
      peticiones.push(
        supa.from(child.table).select("*").eq("tenant_id", cloudState.tenantId).order("posicion")
          .then(r => ({ tipo: "hijo", key, child, data: r.data, error: r.error }))
      );
    }
  }
  const respuestas = await Promise.all(peticiones);

  const padresPorKey = {};
  const hijosPorKey = {};
  for (const r of respuestas) {
    if (r.error) {
      const donde = r.tipo === "padre" ? r.key : r.child.table;
      console.error(`⚠️ Error cargando ${donde} desde Supabase:`, r.error.message);
      continue;
    }
    if (r.tipo === "padre") padresPorKey[r.key] = r.data || [];
    else {
      if (!hijosPorKey[r.key]) hijosPorKey[r.key] = [];
      hijosPorKey[r.key].push({ child: r.child, filas: r.data || [] });
    }
  }

  for (const key of keys) {
    if (!padresPorKey[key]) continue;   // su consulta falló: se conserva lo que hubiera
    const cfg = ENTITY_SCHEMA[key];
    const idField = cfg.idField || "id";
    const objetos = padresPorKey[key].map(cfg.fromRow);
    for (const { child, filas } of (hijosPorKey[key] || [])) {
      // Índice por clave foránea: evita recorrer todas las filas hijas por cada
      // documento (antes era O(padres × hijos); en documentos con muchos ítems
      // eso se notaba tanto como la red).
      const porFk = new Map();
      for (const h of filas) {
        const k = h[child.fk];
        if (!porFk.has(k)) porFk.set(k, []);
        porFk.get(k).push(h);
      }
      objetos.forEach(obj => {
        obj[child.field] = (porFk.get(obj[idField]) || []).map(child.fromRow);
      });
    }
    state.data[key] = objetos;
    // Los objetos recién construidos desde la nube no los muta nadie todavía:
    // sirven como snapshot tal cual, sin una segunda copia de 35 MB.
    fijarSnapshot(key, objetos);
  }

  // Con el catálogo ya en memoria, se piden de una vez las URLs de todas las
  // fotos que viven en el bucket: así la primera pantalla de productos sale con
  // imágenes en vez de irlas pidiendo de a una mientras el usuario mira.
  try {
    const { precargarImagenesProductos } = await import("./imagenProducto.js");
    precargarImagenesProductos();
  } catch (err) {
    console.error("⚠️ No se pudieron precargar las fotos de producto:", err.message);
  }
}

export async function sincronizarEntidadNube(key, nextArrVivo) {
  if (!cloudState.tenantId) return;

  const cfg = ENTITY_SCHEMA[key];
  const idField = cfg.idField || "id";
  const prevArr = __ultimoSnapshotNube[key] || [];
  const prevSerie = __serieSnapshot[key] || new Map();
  const prevPorId = new Map(prevArr.map(p => [p[idField], p]));

  // ─── Congelar AQUÍ, antes de cualquier await ──────────────────────────────
  // El arreglo que llega y los objetos que contiene son los MISMOS que la app
  // sigue usando y mutando. Entre que arranca el upsert y vuelve, el código
  // puede haberle puesto un campo al registro (es justo lo que pasa al emitir
  // una factura: se guarda, se genera el asiento contable y se le asigna
  // `_asientoVenta`). Si el snapshot se toma DESPUÉS del await, se queda con el
  // objeto ya mutado y da por sincronizado un campo que nunca se escribió: el
  // siguiente saveData no ve diferencia y el dato se pierde en la nube para
  // siempre, aunque localStorage sí lo tenga. Le pasó a la MP155.
  //
  // Pero se congela SOLO lo que cambió: copiar el arreglo entero costaba, en
  // productos, duplicar 35 MB de imágenes en cada guardado. Lo que no cambió
  // reutiliza la copia del snapshot anterior, que ya es inmutable.
  const series = nextArrVivo.map(x => JSON.stringify(x));
  const nextIds = new Set(nextArrVivo.map(x => x[idField]));
  const removidos = prevArr.filter(x => !nextIds.has(x[idField]));
  const agregadosOModificados = [];
  const congelados = nextArrVivo.map((x, i) => {
    const id = x[idField];
    const anterior = prevPorId.get(id);
    // Se exige tener el objeto anterior, no solo su serie: si los dos índices
    // se desincronizaran, reutilizar la serie metería un undefined en el
    // snapshot y el siguiente guardado reventaría al leerle el id.
    if (anterior !== undefined && prevSerie.get(id) === series[i]) return anterior;
    const copia = JSON.parse(series[i]);
    agregadosOModificados.push(copia);
    return copia;
  });
  if (removidos.length === 0 && agregadosOModificados.length === 0) return;

  // ─── Freno: un estado a medio cargar no es un borrado ─────────────────────
  // Si la consulta de una tabla falla al arrancar, el pull conserva lo que
  // hubiera en localStorage — y localStorage puede no tener nada (los 35 MB de
  // productos no caben en la cuota del navegador). Con el estado así, el
  // siguiente guardado veía "faltan 90 productos" y los borraba de la nube.
  // En este ERP no existe ningún borrado masivo: se elimina de a uno. Así que
  // muchos registros ausentes de golpe siempre son un estado incompleto.
  const removidosAplicables = removidos.length >= 5 ? [] : removidos;
  if (removidosAplicables.length !== removidos.length) {
    console.error(`⛔ Sincronización de ${key} detenida: el navegador tiene ${nextArrVivo.length} ` +
      `registros y la nube ${prevArr.length}. NO se borró nada. Recarga la página para volver a bajarlos.`);
    try { mostrarErrorSync(key); } catch { /* sin DOM todavía */ }
  }

  try {
    if (agregadosOModificados.length > 0) {
      const filas = agregadosOModificados.map(r => ({ ...cfg.toRow(r), tenant_id: cloudState.tenantId }));
      const { error } = await supa.from(cfg.table).upsert(filas);
      if (error) throw error;
      // Arreglos anidados (contactos, descuentos financieros...): se reemplazan
      // completos por cada padre que cambió — más simple que diferenciar hijo a
      // hijo, y suficiente porque siempre se editan junto con su padre, nunca
      // de forma independiente ni con alta concurrencia entre sí.
      for (const child of (cfg.childTables || [])) {
        for (const padre of agregadosOModificados) {
          const { error: errDel } = await supa.from(child.table).delete().eq(child.fk, padre[idField]).eq("tenant_id", cloudState.tenantId);
          if (errDel) throw errDel;
          const items = padre[child.field] || [];
          if (items.length > 0) {
            const filasHijas = items.map((it, i) => ({ ...child.toRow(it, i), [child.fk]: padre[idField], tenant_id: cloudState.tenantId }));
            const { error: errIns } = await supa.from(child.table).insert(filasHijas);
            if (errIns) throw errIns;
          }
        }
      }
    }
    for (const r of removidosAplicables) {
      const { error } = await supa.from(cfg.table).delete().eq(idField, r[idField]).eq("tenant_id", cloudState.tenantId);
      if (error) throw error; // los hijos se eliminan en cascada (on delete cascade)
    }
    // Solo avanzamos el snapshot si Supabase confirmó los cambios; si algo falla,
    // el próximo saveData() de esta entidad reintentará también lo que quedó pendiente.
    // Lo que el freno no dejó borrar se CONSERVA en el snapshot: sigue en la nube,
    // así que el snapshot tiene que seguir reflejándolo (y volver a avisar).
    const noBorrados = removidos.filter(r => !removidosAplicables.includes(r));
    fijarSnapshot(key, congelados.concat(noBorrados));
    registrarAuditoria(key, agregadosOModificados, removidosAplicables, prevArr, idField);
    mostrarIndicadorSync();
  } catch (error) {
    console.error(`⚠️ Error sincronizando ${key} con Supabase:`, error.message);
    try { mostrarErrorSync(key); } catch { /* sin DOM (arranque): el reintento del próximo saveData mostrará el aviso */ }
  }
}

export let __realtimeChannel = null;

export function suscribirRealtimeTenant(tenantId) {
  if (__realtimeChannel) { supa.removeChannel(__realtimeChannel); __realtimeChannel = null; }
  __realtimeChannel = supa.channel(`erp-${tenantId}`);
  __realtimeChannel.on("postgres_changes",
    { event: "*", schema: "public", table: "tenant_config", filter: `tenant_id=eq.${tenantId}` },
    payload => aplicarConfigRealtime(payload));
  CLOUD_TABLE_KEYS.forEach(key => {
    const cfg = ENTITY_SCHEMA[key];
    __realtimeChannel.on("postgres_changes",
      { event: "*", schema: "public", table: cfg.table, filter: `tenant_id=eq.${tenantId}` },
      payload => aplicarCambioRealtime(key, cfg, payload));
    (cfg.childTables || []).forEach(child => {
      __realtimeChannel.on("postgres_changes",
        { event: "*", schema: "public", table: child.table, filter: `tenant_id=eq.${tenantId}` },
        payload => aplicarCambioHijoRealtime(key, cfg, child, payload));
    });
  });
  __realtimeChannel.subscribe();
}

export function aplicarCambioRealtime(key, cfg, payload) {
  const idField = cfg.idField || "id";
  const list = state.data[key] || [];
  if (payload.eventType === "DELETE") {
    const idBorrado = payload.old[idField];
    state.data[key] = list.filter(x => x[idField] !== idBorrado);
  } else {
    const nuevo = cfg.fromRow(payload.new);
    const idx = list.findIndex(x => x[idField] === nuevo[idField]);
    if (idx >= 0) {
      // La fila padre no trae los arreglos hijos: conservamos los que ya teníamos localmente.
      (cfg.childTables || []).forEach(child => { nuevo[child.field] = list[idx][child.field] || []; });
      list[idx] = nuevo;
    } else {
      (cfg.childTables || []).forEach(child => { nuevo[child.field] = []; });
      list.push(nuevo);
    }
  }
  // Este cambio ya viene confirmado desde Supabase (lo hizo otra sesión, o es el eco
  // de nuestro propio upsert): actualizamos el snapshot para no reenviarlo nosotros.
  fijarSnapshot(key, JSON.parse(JSON.stringify(state.data[key])));
  render();
}

export async function aplicarCambioHijoRealtime(key, cfg, child, payload) {
  const idField = cfg.idField || "id";
  const parentId = (payload.new || payload.old)[child.fk];
  const list = state.data[key] || [];
  const padre = list.find(x => x[idField] === parentId);
  if (!padre) return;
  const { data: hijos, error } = await supa.from(child.table).select("*").eq(child.fk, parentId).order("posicion");
  if (error) { console.error(`⚠️ Error recargando ${child.table}:`, error.message); return; }
  padre[child.field] = (hijos || []).map(child.fromRow);
  fijarSnapshot(key, JSON.parse(JSON.stringify(state.data[key])));
  render();
}

export function getParametrosFiscales() {
  try {
    const raw = localStorage.getItem(PARAMETROS_FISCALES_KEY);
    return raw ? { ...DEFAULT_PARAMETROS_FISCALES, ...JSON.parse(raw) } : { ...DEFAULT_PARAMETROS_FISCALES };
  } catch { return { ...DEFAULT_PARAMETROS_FISCALES }; }
}

// Tarifa general de IVA vigente (Art. 468 E.T.), tomada de Parámetros Fiscales.
// Estaba quemada como 0.19 en una decena de sitios: si la tarifa cambiara —o si
// la empresa vendiera a otra tarifa— habría que perseguir el 19 por todo el
// código, y el parámetro de la pantalla no servía de nada.
export function ivaPct() {
  const v = parseFloat(getParametrosFiscales().iva_general_pct);
  return isNaN(v) ? 19 : v;
}

// IVA de una base gravada, con la tarifa vigente.
export function calcIVA(base) {
  const b = Number(base) || 0;
  return Math.round(b * ivaPct() / 100);
}

export function saveParametrosFiscales(pf) {
  try { localStorage.setItem(PARAMETROS_FISCALES_KEY, JSON.stringify(pf)); } catch {}
}

export function getParametrosNomina() {
  try {
    const raw = localStorage.getItem(PARAMETROS_NOMINA_KEY);
    return raw ? { ...DEFAULT_PARAMETROS_NOMINA, ...JSON.parse(raw) } : { ...DEFAULT_PARAMETROS_NOMINA };
  } catch { return { ...DEFAULT_PARAMETROS_NOMINA }; }
}

export function saveParametrosNomina(pn) {
  try { localStorage.setItem(PARAMETROS_NOMINA_KEY, JSON.stringify(pn)); } catch {}
}

export function getNextNumber(prefix, list) {
  const max = list.reduce((m, d) => {
    const match = (d.numero||"").match(/(\d+)$/);
    return match ? Math.max(m, parseInt(match[1])) : m;
  }, 0);
  return prefix + "-" + String(max+1).padStart(4, "0");
}

export async function siguienteNumeroRaw(prefijo) {
  if (!cloudState.tenantId) {
    throw new Error("No hay sesión activa: no se puede asignar un número de documento.");
  }
  const { data, error } = await supa.rpc("siguiente_numero_doc", {
    p_tenant_id: cloudState.tenantId,
    p_prefijo: prefijo
  });
  if (error) {
    console.error("⚠️ Error asignando número de documento:", error.message);
    throw new Error("No se pudo asignar el número de documento. Verifica tu conexión e intenta de nuevo.");
  }
  return data;
}

export async function siguienteNumeroDoc(prefix) {
  const n = await siguienteNumeroRaw(prefix);
  return prefix + "-" + String(n).padStart(4, "0");
}

export async function establecerNumeroDoc(prefijo, valor) {
  if (!cloudState.tenantId) throw new Error("No hay sesión activa.");
  const { data, error } = await supa.rpc("establecer_numero_doc", {
    p_tenant_id: cloudState.tenantId,
    p_prefijo: prefijo,
    p_valor: valor
  });
  if (error) {
    console.error("⚠️ Error fijando consecutivo:", error.message);
    throw new Error("No se pudo actualizar el consecutivo. Verifica tu conexión e intenta de nuevo.");
  }
  return data;
}

export async function cargarConsecutivosActuales() {
  if (!cloudState.tenantId) return {};
  const { data, error } = await supa.from("contador_documentos").select("prefijo, ultimo").eq("tenant_id", cloudState.tenantId);
  if (error) { console.error("⚠️ Error cargando consecutivos:", error.message); return {}; }
  const mapa = {};
  (data || []).forEach(r => { mapa[r.prefijo] = r.ultimo; });
  return mapa;
}

// ─── Cierre contable: automático, el último día de cada mes ─────────────────
// Antes la fecha se fijaba a mano y eso salía caro: bastaba escribir un día que
// todavía no había llegado para dejar a todo el mundo sin poder registrar nada
// en el mes corriente. Ya no se configura: el periodo se cierra solo cuando el
// mes termina.
//
// El cierre vigente es SIEMPRE el último día del mes anterior — el 31, el 30,
// el 28 o el 29, tal cual cae, sin correrlo porque sea domingo o festivo. Así
// el mes en curso está disponible hasta su último día y, apenas empieza el
// siguiente, el anterior queda cerrado.
//
// La tabla `cierre_contable` quedó sin uso: la fecha se calcula, no se guarda.
export function fechaCierreVigente() {
  const [anio, mes] = todayCO().split("-").map(Number);
  // Día 0 de un mes = último día del mes anterior. En UTC, para que no dependa
  // de la zona horaria del navegador.
  return new Date(Date.UTC(anio, mes - 1, 0)).toISOString().split("T")[0];
}

export function fechaEstaCerrada(fecha) {
  if (!fecha) return false;
  return fecha <= fechaCierreVigente();
}

export function ensurePUC() {
  if (!state.data.puc) state.data.puc = [];
  const existentes = new Set(state.data.puc.map(c => c.codigo));
  const faltantes  = PUC_DEFAULT.filter(c => !existentes.has(c.codigo));
  if (faltantes.length > 0) {
    state.data.puc = [...state.data.puc, ...faltantes.map(c => ({ ...c }))];
    saveData("puc", state.data.puc);
  }
}

export function loadData() {
  // Detectar si localStorage está bloqueado (WKWebView, Safari privado, etc.)
  try {
    localStorage.setItem("__erp_test__", "1");
    localStorage.removeItem("__erp_test__");
  } catch(_lsErr) {
    // localStorage no disponible — mostrar aviso claro
    const root = document.getElementById("root");
    if (root) {
      root.innerHTML = `<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f8fafc;font-family:sans-serif;padding:20px;">
        <div style="background:white;border-radius:16px;box-shadow:0 4px 32px rgba(0,0,0,.12);padding:32px;max-width:420px;width:100%;text-align:center;">
          <div style="font-size:52px;margin-bottom:12px;">🔒</div>
          <h2 style="color:#12264D;margin:0 0 10px;">Almacenamiento bloqueado</h2>
          <p style="color:#4b5563;font-size:14px;line-height:1.6;margin:0 0 16px;">
            ${BRAND.producto} no puede guardar datos porque tu navegador está bloqueando el almacenamiento local.<br><br>
            <strong>En iPhone/iPad:</strong> Abre este archivo directamente en <strong>Safari</strong> (no desde WhatsApp, Telegram o correo).<br><br>
            Desde la app <strong>Archivos</strong> → mantén presionado el archivo → <em>Compartir → Abrir en Safari</em>
          </p>
          <div style="background:#fef3c7;border-radius:10px;padding:12px;font-size:13px;color:#92400e;">
            ⚠️ Si estás en modo incógnito, el almacenamiento siempre está desactivado.
          </div>
        </div>
      </div>`;
    }
    return;
  }
  Object.entries(KEYS).forEach(([k, sk]) => {
    try {
      const raw = localStorage.getItem(sk);
      state.data[k] = raw ? JSON.parse(raw) : [];
    } catch {
      state.data[k] = [];
    }
  });
  try {
    state.page = localStorage.getItem("erp:page") || "dashboard";
  } catch {}
  try {
    const rawCfg = localStorage.getItem("erp:alert_config");
    if (rawCfg) state.alertConfig = { ...state.alertConfig, ...JSON.parse(rawCfg) };
  } catch {}
  // Migrar el número único anterior (alertConfig.whatsapp) a la lista de números
  if ((!state.alertConfig.whatsappNumbers || state.alertConfig.whatsappNumbers.length === 0) && state.alertConfig.whatsapp) {
    state.alertConfig.whatsappNumbers = [{ id: genId(), nombre: "Principal", numero: String(state.alertConfig.whatsapp).replace(/[^0-9]/g, "") }];
    state.alertConfig.numeroEnvioId = state.alertConfig.whatsappNumbers[0].id;
    saveAlertConfig();
  }
  // Migrar selección única (numeroEnvioId) a selección múltiple (numerosEnvioIds)
  if ((!state.alertConfig.numerosEnvioIds || state.alertConfig.numerosEnvioIds.length === 0)) {
    const ids = (state.alertConfig.whatsappNumbers||[]).map(n => n.id);
    if (state.alertConfig.numeroEnvioId && ids.includes(state.alertConfig.numeroEnvioId)) {
      state.alertConfig.numerosEnvioIds = [state.alertConfig.numeroEnvioId];
    } else if (ids.length > 0) {
      state.alertConfig.numerosEnvioIds = [ids[0]];
    } else {
      state.alertConfig.numerosEnvioIds = [];
    }
    saveAlertConfig();
  }
  // Asegurar que no queden referencias a números eliminados
  {
    const idsValidos = new Set((state.alertConfig.whatsappNumbers||[]).map(n => n.id));
    state.alertConfig.numerosEnvioIds = (state.alertConfig.numerosEnvioIds||[]).filter(id => idsValidos.has(id));
  }
  try {
    const rawEmp = localStorage.getItem("erp:empresa_config");
    if (rawEmp) Object.assign(EMPRESA, JSON.parse(rawEmp));
  } catch {}
  aplicarLogoEmpresa();
  try {
    const rawIA = localStorage.getItem("erp:ia_config");
    if (rawIA) state.iaConfig = { ...state.iaConfig, ...JSON.parse(rawIA) };
    // La API key de Anthropic ya no vive en el cliente (se movió a un secreto de
    // servidor detrás de la Edge Function ia-proxy): purgar cualquier resto que
    // haya quedado guardado en el navegador desde antes de este cambio.
    if (state.iaConfig && "apiKey" in state.iaConfig) {
      delete state.iaConfig.apiKey;
      try { localStorage.setItem("erp:ia_config", JSON.stringify(state.iaConfig)); } catch {}
    }
  } catch {}
  // Garantizar PUC completo: agrega cuentas faltantes, nunca borra las personalizadas
  ensurePUC();
}

export function saveAlertConfig() {
  try { localStorage.setItem("erp:alert_config", JSON.stringify(state.alertConfig)); } catch {}
}

export function saveIAConfig() {
  try { localStorage.setItem("erp:ia_config", JSON.stringify(state.iaConfig)); } catch {}
}

export function saveEmpresaConfig() {
  try { localStorage.setItem("erp:empresa_config", JSON.stringify(EMPRESA)); } catch {}
}

// Se avisa UNA vez por sesión: si no cabe, no va a caber en el siguiente
// guardado tampoco, y repetirlo en cada tecla sería insufrible.
let __avisadoSinEspacio = false;
function avisarSinEspacioLocal(key) {
  if (__avisadoSinEspacio) return;
  __avisadoSinEspacio = true;
  console.error(`⛔ No cabe ${key} en el almacenamiento del navegador.`);
  try {
    alert("⚠️ El navegador se quedó sin espacio para la copia local de los datos.\n\n" +
      "Tu trabajo SÍ se está guardando en la nube, pero esta pestaña puede mostrar información " +
      "incompleta hasta que recargues.\n\n" +
      "Causa habitual: fotos de productos muy pesadas. Avísale a Andrés para que las optimice.");
  } catch { /* sin DOM */ }
}

export function saveData(key, val) {
  // Actualizar state
  state.data[key] = val;

  // Guardar en localStorage con verificación
  try {
    localStorage.setItem(KEYS[key], JSON.stringify(val));
    // Verificar que se guardó correctamente
    const verificacion = JSON.parse(localStorage.getItem(KEYS[key]));
    if (!verificacion || verificacion.length !== val.length) {
      console.error(`⚠️ Error al guardar ${key}: mismatch de cantidad`);
    }
  } catch (error) {
    // El navegador da ~5-10 MB por sitio y las fotos de los productos son
    // base64: cuando no caben, setItem lanza y ANTES esto solo se escribía en la
    // consola. El efecto se veía mucho después y en otra parte: al recargar, la
    // copia local quedaba vieja o vacía, la pantalla mostraba una fracción de
    // los datos, y el guardado siguiente creía que el resto se había borrado.
    // La nube no se ve afectada (se sincroniza aparte), pero hay que decirlo.
    console.error(`❌ Error al guardar en localStorage (${key}):`, error);
    const sinEspacio = error && (error.name === "QuotaExceededError" ||
      error.name === "NS_ERROR_DOM_QUOTA_REACHED" || /quota/i.test(error.message || ""));
    if (sinEspacio) avisarSinEspacioLocal(key);
  }

  // Entidades ya migradas a tabla propia: sincronizar por registro en vez de
  // depender del snapshot único (ver CLOUD_TABLE_KEYS más arriba).
  if (CLOUD_TABLE_KEYS.has(key)) rastrearSincronizacion(encadenarSyncEntidad(key, val));
}

// Reinicio total del tenant en la nube (botón "Limpiar todo y reiniciar").
// Usa la RPC reiniciar_tenant (security definer, solo admin) porque el borrado
// fila a fila desde el cliente no puede eliminar facturas/NC/OCP emitidas
// (RLS solo permite borrar borradores) y esos documentos reaparecerían al
// volver a iniciar sesión. La RPC además reinicia los consecutivos, levanta el
// cierre contable y vacía el snapshot legado (erp_data).
export async function reiniciarTenantNube() {
  if (!cloudState.tenantId) throw new Error("No hay sesión activa.");
  const { error } = await supa.rpc("reiniciar_tenant", { p_tenant_id: cloudState.tenantId });
  if (error) {
    console.error("⚠️ Error reiniciando el tenant en la nube:", error.message);
    throw new Error(error.message);
  }
  // La nube quedó vacía: reiniciar el snapshot para que el próximo saveData
  // de cada entidad (p.ej. la re-siembra del PUC) suba todo como filas nuevas.
  CLOUD_TABLE_KEYS.forEach(key => { fijarSnapshot(key, []); });
  state._consecutivosCache = {};
  mostrarIndicadorSync();
}

export function deleteData(key, id) {
  if (!state.data[key]) return false;
  
  const arrayAntes = state.data[key];
  const arrayDespues = arrayAntes.filter(item => item.id !== id);
  
  if (arrayDespues.length < arrayAntes.length) {
    saveData(key, arrayDespues);
    return true;
  }
  return false;
}
